import React from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { FileText, Image as ImageIcon, Download, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { key: "all", label: "All" },
  { key: "photo", label: "Photos" },
  { key: "agenda", label: "Agenda" },
  { key: "document", label: "Downloads" },
];

export default function MediaPage() {
  const { t } = useTranslation();
  const [items, setItems] = React.useState([]);
  const [filter, setFilter] = React.useState("all");
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    axios.get(`${API}/media`)
      .then((r) => setItems(r.data))
      .catch(() => toast.error("Failed to load media"))
      .finally(() => setLoading(false));
  }, []);

  const shown = filter === "all" ? items : items.filter((m) => m.category === filter);
  const isImg = (m) => (m.content_type || "").startsWith("image/");

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
      <div className="tag-mono mb-1">{t("nav.media")}</div>
      <h1 className="font-serif-diplo text-2xl sm:text-3xl text-[color:var(--ink)]">{t("media.title")}</h1>
      <div className="gold-underline mt-3 mb-5" />

      <div className="flex flex-wrap gap-1.5 mb-5">
        {CATEGORIES.map((c) => (
          <button
            key={c.key}
            data-testid={`media-tab-${c.key}`}
            data-active={filter === c.key}
            onClick={() => setFilter(c.key)}
            className="filter-pill filter-pill-sm"
          >
            {c.label} ({c.key === "all" ? items.length : items.filter((m) => m.category === c.key).length})
          </button>
        ))}
      </div>

      {loading ? (
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
          {[1, 2, 3].map((i) => <div key={i} className="h-48 rounded-2xl bg-white/60 animate-pulse" />)}
        </div>
      ) : shown.length === 0 ? (
        <div className="text-center text-[color:var(--ink-3)] font-mono-diplo text-xs py-16" data-testid="media-empty">
          {t("media.empty")}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3 stagger">
          {shown.map((m) => (
            <div key={m.id} data-testid={`media-item-${m.id}`} className="diplomatic-card overflow-hidden">
              {isImg(m) ? (
                <a href={`${API}/media/${m.id}/file`} target="_blank" rel="noreferrer">
                  <img src={`${API}/media/${m.id}/file`} alt={m.original_filename} className="w-full h-44 object-cover" />
                </a>
              ) : (
                <a href={`${API}/media/${m.id}/file`} target="_blank" rel="noreferrer"
                   className="w-full h-44 bg-[color:var(--forest-soft)] flex flex-col items-center justify-center gap-2 hover:bg-[color:var(--forest)]/10 transition-colors">
                  <FileText className="w-10 h-10 text-[color:var(--forest)]" />
                  <span className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--forest-dark)]">
                    {(m.original_filename || "").split(".").pop() || "file"}
                  </span>
                </a>
              )}
              <div className="p-3 flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-[color:var(--ink)] truncate font-semibold">{m.original_filename}</div>
                  <div className="text-[10px] text-[color:var(--ink-3)] mt-0.5 flex items-center gap-1">
                    {isImg(m) ? <ImageIcon className="w-3 h-3" /> : <FileText className="w-3 h-3" />}
                    <span className="capitalize">{m.category || "photo"}</span>
                    <span>·</span>
                    <span>{(m.size / 1024).toFixed(0)} KB</span>
                  </div>
                </div>
                <a href={`${API}/media/${m.id}/file`} download={m.original_filename}
                   data-testid={`media-download-${m.id}`}
                   className="p-1.5 rounded-md hover:bg-[color:var(--forest-soft)] text-[color:var(--forest)]">
                  <Download className="w-4 h-4" />
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
